import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  deviceAddModalRecoil,
  LoadingRecoil,
} from '@/atom/ModalAtom';
import {
  deviceListRecoil,
  deviceSiteListRecoil,
  deviceModelListRecoil,
} from '@/atom/DeviceAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import TitleTextBox from '@/component/TitleTextBox';
import Pagenation from '@/component/Pagenation';
import SearchBtn from '@/component/SearchBtn';
import DeleteModal from '@/modal/DeleteModal';
import DeviceAddModal from '@/modal/DeviceAddModal';
import DeviceCommandModal from '@/modal/DeviceCommandAddModal';
import LabelType1 from '@/component/LabelType1';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { DeviceApi } from '@/sdk/DeviceApi';
import {
  DeviceControllerGetDevicesParams,
  DeleteDeviceDtoTx,
  Devices,
  DeviceControllerCreateReportParams,
  DeviceControllerCreateReportWtihOptionsParams,
  GetDeviceReportDtoRx,
} from '@/sdk/data-contracts';
import Loading from '@/component/Loading';

const Device = () => {
  const deviceApi = new DeviceApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [loadingState, setLoadingState] = useRecoilState(LoadingRecoil);
  const [deviceAddModalState, setDeviceAddModalState] =
    useRecoilState(deviceAddModalRecoil);
  const [deviceModelListState, setDeviceModelListState] = useRecoilState(
    deviceModelListRecoil,
  );
  const [deviceSiteListState, setDeviceSiteListState] =
    useRecoilState(deviceSiteListRecoil);
  const [deviceListState, setDeviceListState] =
    useRecoilState(deviceListRecoil);

  const [modelName, setModelName] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [siteName, setSiteName] = useState<string>('');
  const [facilityName, setFacilityName] = useState<string>('');
  const [communicationStatus, setCommunicationStatus] = useState<string>('');
  const [sensorStatus, setSensorStatus] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [reportStartDate, setReportStartDate] = useState<string>('');
  const [reportEndDate, setReportEndDate] = useState<string>('');
  const [number, setNumber] = useState<string>('');
  const [devEui, setDevEui] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');
  const [checkItems, setCheckItems] = useState<number[]>([]);
  const [listLength, setListLength] = useState<number>(0);

  const allCheckBtnRef = useRef<HTMLInputElement>(null);

  const downloadCSV = async (saveFileName: string) => {
    let allList: Devices[] | undefined;

    const dto: DeviceControllerGetDevicesParams = {
      sortBy,
      order,
    };

    try {
      const { data } = await deviceApi.deviceControllerGetDevices(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.devices) allList = data.devices;
    } catch (err: any) {}

    if (!allList || allList.length === 0) {
      setErrorModalText('다운로드할 데이터가 없습니다.');
      return;
    }

    const headers = [
      'No',
      '모델명',
      '디바이스 No',
      'Dev EUI',
      '디바이스 명',
      '시설명',
      '통신상태',
      '센서상태',
      '마지막 수신 일자',
    ];

    const rows = allList.map((list, index) => [
      index + 1,
      list.modelName,
      list.number,
      list.devEui,
      list.name,
      list.facilityName,
      list.communicationStatus,
      list.sensorStatus,
      list.lastDataReceivedAt,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const bom = '\uFEFF';
    const csvWithBom = bom + csvContent;

    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${saveFileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchModelAndSiteList = async () => {
    try {
      const { data } = await deviceApi.deviceControllerGetDeviceDataArray({
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.modelNames) setDeviceModelListState(data.modelNames);
      if (data.siteNames) setDeviceSiteListState(data.siteNames);
    } catch (err: any) {}
  };

  const filterDeviceList = async () => {
    const dto: DeviceControllerGetDevicesParams = {
      modelName,
      name,
      siteName,
      facilityName,
      ...(communicationStatus && { communicationStatus }),
      ...(sensorStatus && { sensorStatus }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      number,
      devEui,
      offset,
      limit,
      sortBy,
      order,
    };

    try {
      const { data } = await deviceApi.deviceControllerGetDevices(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      console.log(data);
      if (data.devices) setDeviceListState(data.devices);
      if (data.totalColumn) {
        setListLength(data.totalColumn);
      } else if (data.totalColumn === 0) {
        setListLength(0);
      }
    } catch (err: any) {}
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCheckItems([]);
    setOffset(0);
    filterDeviceList();
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLimit(Number(e.target.value));
    setOffset(0);
  };

  const handleOffsetChange = (value: number) => {
    setOffset((value - 1) * limit);
    setCheckItems([]);
    if (allCheckBtnRef.current) {
      allCheckBtnRef.current.checked = false;
    }
  };

  const handleSingleCheck = (checked: boolean, id: number) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked) {
      const idArray: number[] = [];
      deviceListState?.forEach((el) => idArray.push(Number(el.id)));
      setCheckItems(idArray);
    } else {
      setCheckItems([]);
    }
  };

  const handleSelectDeleteNumCheck = () => {
    if (checkItems.length === 0) {
      setErrorModalText('선택된 항목이 없습니다.');
      return;
    }
    setDeleteModalState(true);
  };

  const handleSelectDelete = async () => {
    try {
      const dto: DeleteDeviceDtoTx = {
        ids: checkItems,
      };
      await deviceApi.deviceControllerDeleteDevice(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  // 리포트 생성 날짜만 선택하여 리포트 생성
  // const openAndSavePDF = async () => {
  //   let pdfDatas: GetDeviceReportDtoRx[] = [];

  //   if (reportStartDate === '' || reportEndDate === '') {
  //     setErrorModalText('조회할 기간을 선택해 주세요.');
  //     return;
  //   }

  //   const dto: DeviceControllerCreateReportParams = {
  //     startDate: reportStartDate,
  //     endDate: reportEndDate,
  //   };

  //   try {
  //     const { data } = await deviceApi.deviceControllerCreateReport(dto, {
  //       headers: {
  //         Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });
  //     pdfDatas = data;
  //   } catch (err: any) {}

  //   if (pdfDatas.length === 0) {
  //     setErrorModalText('리포트를 생성할 데이터가 없습니다.');
  //     return;
  //   }

  //   setLoadingState(true);

  //   const pdf = new jsPDF('p', 'mm', 'a4');

  //   for (let i = 0; i < pdfDatas.length; i++) {
  //     const firstPageSize = 30;
  //     const subsequentPageSize = 36;

  //     const totalDataLength = pdfDatas[i].datas.length;

  //     const firstChunk = pdfDatas[i].datas.slice(0, firstPageSize);

  //     const remainingData = pdfDatas[i].datas.slice(firstPageSize);
  //     const totalChunks = Math.ceil(remainingData.length / subsequentPageSize);

  //     const pdfContentFirstPage = document.createElement('div');
  //     pdfContentFirstPage.style.width = '210mm';
  //     pdfContentFirstPage.style.height = '297mm';
  //     pdfContentFirstPage.innerHTML = `
  //       <div class='device-pdf-box'>
  //         <h1>${i + 1}. ${pdfDatas[i].modelName} 운영현황</h1>
  //         <ul>
  //           <li>- 수량 : 총 ${pdfDatas[i].datas.length}대</li>
  //           <li>- 기간 : ${reportStartDate} ~ ${reportEndDate}</li>
  //         </ul>
  //         <table>
  //           <thead>
  //             <tr>
  //               <th>Device Name</th>
  //               ${pdfDatas[i].datas[0].data.map((data: { dataName: string }) => `<th>${data.dataName}</th>`).join('')}
  //             </tr>
  //           </thead>
  //           <tbody>
  //             ${firstChunk
  //               .map(
  //                 (dataItem) => `
  //                 <tr>
  //                   <td>${dataItem.deviceName}</td>
  //                   ${dataItem.data.map((data: { dataValue: number }) => `<td>${data.dataValue}</td>`).join('')}
  //                 </tr>
  //               `,
  //               )
  //               .join('')}
  //           </tbody>
  //         </table>
  //       </div>
  //     `;

  //     document.body.appendChild(pdfContentFirstPage);

  //     const canvasFirstPage = await html2canvas(pdfContentFirstPage);
  //     const imgDataFirstPage = canvasFirstPage.toDataURL('image/png');
  //     const imgWidth = 190;
  //     const imgHeight =
  //       (canvasFirstPage.height * imgWidth) / canvasFirstPage.width;
  //     const position = 10;

  //     if (i > 0) pdf.addPage();
  //     pdf.addImage(imgDataFirstPage, 'PNG', 10, position, imgWidth, imgHeight);

  //     document.body.removeChild(pdfContentFirstPage);

  //     for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
  //       const start = chunkIndex * subsequentPageSize;
  //       const end = start + subsequentPageSize;
  //       const currentChunk = remainingData.slice(start, end);

  //       const pdfContent = document.createElement('div');
  //       pdfContent.style.width = '210mm';
  //       pdfContent.style.height = '297mm';
  //       pdfContent.innerHTML = `
  //         <div class='device-pdf-box'>
  //           <table>
  //             <thead>
  //               <tr>
  //                 <th>Device Name</th>
  //                 ${pdfDatas[i].datas[0].data.map((data: { dataName: string }) => `<th>${data.dataName}</th>`).join('')}
  //               </tr>
  //             </thead>
  //             <tbody>
  //               ${currentChunk
  //                 .map(
  //                   (dataItem) => `
  //                   <tr>
  //                     <td>${dataItem.deviceName}</td>
  //                     ${dataItem.data.map((data: { dataValue: number }) => `<td>${data.dataValue}</td>`).join('')}
  //                   </tr>
  //                 `,
  //                 )
  //                 .join('')}
  //             </tbody>
  //           </table>
  //         </div>
  //       `;

  //       document.body.appendChild(pdfContent);

  //       const canvas = await html2canvas(pdfContent);
  //       const imgData = canvas.toDataURL('image/png');
  //       const imgHeight = (canvas.height * imgWidth) / canvas.width;

  //       pdf.addPage();
  //       pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);

  //       document.body.removeChild(pdfContent);
  //     }
  //   }

  //   setLoadingState(false);
  //   pdf.save('device.pdf');
  // };

  // 위 검색 옵션 포함 리포트 생성 날짜와 같이 리포트 생성
  const openAndSavePDF = async () => {
    let pdfDatas: GetDeviceReportDtoRx[] = [];

    if (reportStartDate === '' || reportEndDate === '') {
      setErrorModalText('조회할 기간을 선택해 주세요.');
      return;
    }

    const dto: DeviceControllerCreateReportWtihOptionsParams = {
      modelName,
      deviceName: name,
      siteName,
      facilityName,
      ...(communicationStatus && { communicationStatus }),
      ...(sensorStatus && { sensorStatus }),
      ...(startDate && { setupDateStart: startDate }),
      ...(endDate && { setupDateEnd: endDate }),
      deviceNo: number,
      devEui,
      startDate: reportStartDate,
      endDate: reportEndDate,
    };

    try {
      const { data } = await deviceApi.deviceControllerCreateReportWtihOptions(
        dto,
        {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        },
      );
      pdfDatas = data;
    } catch (err: any) {}

    if (pdfDatas.length === 0) {
      setErrorModalText('리포트를 생성할 데이터가 없습니다.');
      return;
    }

    setLoadingState(true);

    const pdf = new jsPDF('p', 'mm', 'a4');

    for (let i = 0; i < pdfDatas.length; i++) {
      const firstPageSize = 30;
      const subsequentPageSize = 36;

      const totalDataLength = pdfDatas[i].datas.length;

      const firstChunk = pdfDatas[i].datas.slice(0, firstPageSize);

      const remainingData = pdfDatas[i].datas.slice(firstPageSize);
      const totalChunks = Math.ceil(remainingData.length / subsequentPageSize);

      const pdfContentFirstPage = document.createElement('div');
      pdfContentFirstPage.style.width = '210mm';
      pdfContentFirstPage.style.height = '297mm';
      pdfContentFirstPage.innerHTML = `
        <div class='device-pdf-box'>
          <h1>${i + 1}. ${pdfDatas[i].modelName} 운영현황</h1>
          <ul>
            <li>- 수량 : 총 ${pdfDatas[i].datas.length}대</li>
            <li>- 기간 : ${reportStartDate} ~ ${reportEndDate}</li>
          </ul>
          <table>
            <thead>
              <tr>
                <th>Device Name</th>
                ${pdfDatas[i].datas[0].data.map((data: { dataName: string }) => `<th>${data.dataName}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${firstChunk
                .map(
                  (dataItem) => `
                  <tr>
                    <td>${dataItem.deviceName}</td>
                    ${dataItem.data.map((data: { dataValue: number }) => `<td>${data.dataValue}</td>`).join('')}
                  </tr>
                `,
                )
                .join('')}
            </tbody>
          </table>
        </div>
      `;

      document.body.appendChild(pdfContentFirstPage);

      const canvasFirstPage = await html2canvas(pdfContentFirstPage);
      const imgDataFirstPage = canvasFirstPage.toDataURL('image/png');
      const imgWidth = 190;
      const imgHeight =
        (canvasFirstPage.height * imgWidth) / canvasFirstPage.width;
      const position = 10;

      if (i > 0) pdf.addPage();
      pdf.addImage(imgDataFirstPage, 'PNG', 10, position, imgWidth, imgHeight);

      document.body.removeChild(pdfContentFirstPage);

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * subsequentPageSize;
        const end = start + subsequentPageSize;
        const currentChunk = remainingData.slice(start, end);

        const pdfContent = document.createElement('div');
        pdfContent.style.width = '210mm';
        pdfContent.style.height = '297mm';
        pdfContent.innerHTML = `
          <div class='device-pdf-box'>
            <table>
              <thead>
                <tr>
                  <th>Device Name</th>
                  ${pdfDatas[i].datas[0].data.map((data: { dataName: string }) => `<th>${data.dataName}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${currentChunk
                  .map(
                    (dataItem) => `
                    <tr>
                      <td>${dataItem.deviceName}</td>
                      ${dataItem.data.map((data: { dataValue: number }) => `<td>${data.dataValue}</td>`).join('')}
                    </tr>
                  `,
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        `;

        document.body.appendChild(pdfContent);

        const canvas = await html2canvas(pdfContent);
        const imgData = canvas.toDataURL('image/png');
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);

        document.body.removeChild(pdfContent);
      }
    }

    setLoadingState(false);
    pdf.save('device.pdf');
  };

  useEffect(() => {
    fetchModelAndSiteList();
    filterDeviceList();
  }, [limit, offset]);

  return (
    <>
      <CntBox1>
        <TitleTextBox text="디바이스" />
        <form onSubmit={handleSearch}>
          <SearchOptionBox>
            <InputWrap>
              <InputArea>
                <IS.SelectType1>
                  <select
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">디바이스 모델</option>
                    {deviceModelListState.map((list, index) => (
                      <option value={list} key={index}>
                        {list}
                      </option>
                    ))}
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.TextInputType1
                  style={{ width: 153 }}
                  type="text"
                  placeholder="디바이스명"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <IS.SelectType1>
                  <select
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    className="selected"
                    style={{ width: 153 }}
                  >
                    <option value="">사이트</option>
                    {deviceSiteListState.map((list, index) => (
                      <option value={list} key={index}>
                        {list}
                      </option>
                    ))}
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.TextInputType1
                  style={{ width: 320 }}
                  type="text"
                  placeholder="시설명"
                  value={facilityName}
                  onChange={(e) => setFacilityName(e.target.value)}
                />
                <IS.SelectType1>
                  <select
                    value={communicationStatus}
                    onChange={(e) => setCommunicationStatus(e.target.value)}
                    className="selected"
                    style={{ width: 120 }}
                  >
                    <option value="">통신상태</option>
                    <option value="정상">정상</option>
                    <option value="통신이상">통신이상</option>
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
                <IS.SelectType1>
                  <select
                    value={sensorStatus}
                    onChange={(e) => setSensorStatus(e.target.value)}
                    className="selected"
                    style={{ width: 120 }}
                  >
                    <option value="">센서상태</option>
                    <option value="정상">정상</option>
                    <option value="센서이상">센서이상</option>
                  </select>
                  <span className="img-box">
                    <img src="/img/arrow_bottom_555.svg" alt="" />
                  </span>
                </IS.SelectType1>
              </InputArea>
              <InputArea>
                <h2>설치일자</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    if (endDate !== '') {
                      if (e.target.value > endDate) {
                        setErrorModalText(
                          '시작일은 종료일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setStartDate(e.target.value);
                  }}
                />
                <h2>~</h2>
                <IS.TextInputType1
                  style={{ width: 145 }}
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    if (startDate !== '') {
                      if (e.target.value < startDate) {
                        setErrorModalText(
                          '종료일은 시작일보다 클 수 없습니다.',
                        );
                        return;
                      }
                    }
                    setEndDate(e.target.value);
                  }}
                />
                <IS.TextInputType1
                  style={{ width: 336 }}
                  type="text"
                  placeholder="디바이스 No"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                />
                <IS.TextInputType1
                  style={{ width: 337 }}
                  type="text"
                  placeholder="Dev EUI"
                  value={devEui}
                  onChange={(e) => setDevEui(e.target.value)}
                />
              </InputArea>
            </InputWrap>
            <SearchBtn />
          </SearchOptionBox>
        </form>
        <ReportBox>
          <IS.TextInputType1
            style={{ width: 145 }}
            type="date"
            value={reportStartDate}
            onChange={(e) => {
              if (reportEndDate !== '') {
                if (e.target.value > reportEndDate) {
                  setErrorModalText('시작일은 종료일보다 클 수 없습니다.');
                  return;
                }
              }
              setReportStartDate(e.target.value);
            }}
          />
          <h2>~</h2>
          <IS.TextInputType1
            style={{ width: 145 }}
            type="date"
            value={reportEndDate}
            onChange={(e) => {
              if (reportStartDate !== '') {
                if (e.target.value < reportStartDate) {
                  setErrorModalText('종료일은 시작일보다 클 수 없습니다.');
                  return;
                }
              }
              setReportEndDate(e.target.value);
            }}
          />
          <BS.Btn1Type1 type="button" onClick={openAndSavePDF}>
            리포트 생성
          </BS.Btn1Type1>
        </ReportBox>
        <TableOpitonBox>
          <h1>
            전체 <span>{listLength.toLocaleString()}</span>
          </h1>
          <div className="right-box">
            <IS.SelectType1>
              <select
                value={limit}
                onChange={handleLimitChange}
                className="selected"
                style={{ width: 153 }}
              >
                <option value={10}>10개씩 보기</option>
                <option value={20}>20개씩 보기</option>
                <option value={30}>30개씩 보기</option>
                <option value={50}>50개씩 보기</option>
              </select>
              <span className="img-box">
                <img src="/img/arrow_bottom_555.svg" alt="" />
              </span>
            </IS.SelectType1>
            <BS.Btn1Type5
              type="button"
              onClick={() => downloadCSV('device_list')}
            >
              Excel 다운로드
              <span className="img-box">
                <img src="/img/ico_down_fff.svg" alt="" />
              </span>
            </BS.Btn1Type5>
            <BS.Btn1Type1
              type="button"
              onClick={() => setDeviceAddModalState(true)}
            >
              등록
            </BS.Btn1Type1>
            <BS.Btn1Type2 type="button" onClick={handleSelectDeleteNumCheck}>
              선택 삭제
            </BS.Btn1Type2>
          </div>
        </TableOpitonBox>
        <TableBox>
          <table>
            <thead>
              <tr>
                <th>
                  <IS.CheckBoxType1>
                    <input
                      type="checkbox"
                      id="all"
                      onChange={(e) => handleAllCheck(e.target.checked)}
                      ref={allCheckBtnRef}
                      checked={
                        checkItems.length === deviceListState?.length &&
                        deviceListState?.length !== 0
                          ? true
                          : false
                      }
                    />
                    <label htmlFor="all" className="img-box">
                      <img src="/img/ico_check_1.svg" alt="" />
                    </label>
                  </IS.CheckBoxType1>
                </th>
                <th>No</th>
                <th>모델명</th>
                <th>디바이스 No</th>
                <th>Dev EUI</th>
                <th>디바이스 명</th>
                <th>시설명</th>
                <th>통신상태</th>
                <th>센서상태</th>
                <th>마지막 수신 일자</th>
              </tr>
            </thead>
            <tbody>
              {deviceListState?.map((list, index) => (
                <tr key={index}>
                  <td>
                    <IS.CheckBoxType1>
                      <input
                        type="checkbox"
                        id={`cv-${index}`}
                        onChange={(e) =>
                          handleSingleCheck(e.target.checked, Number(list.id))
                        }
                        checked={
                          checkItems.includes(Number(list.id)) ? true : false
                        }
                      />
                      <label htmlFor={`cv-${index}`} className="img-box">
                        <img src="/img/ico_check_1.svg" alt="" />
                      </label>
                    </IS.CheckBoxType1>
                  </td>
                  <td>{(offset + index + 1).toLocaleString()}</td>
                  <td>{list.modelName}</td>
                  <td
                    className="button-text"
                    onClick={() => navigate(`/device/detail/${list.id}`)}
                  >
                    {list.number}
                  </td>
                  <td>{list.devEui}</td>
                  <td>{list.name}</td>
                  <td>{list.facilityName}</td>
                  <td>
                    {list.communicationStatus !== '' && (
                      <LabelType1 labelValue={list.communicationStatus} />
                    )}
                  </td>
                  <td>
                    {list.sensorStatus !== '' && (
                      <LabelType1 labelValue={list.sensorStatus} />
                    )}
                  </td>
                  <td>{list.lastDataReceivedAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableBox>
        {listLength !== 0 && (
          <Pagenation
            listAllLength={listLength}
            listLimit={limit}
            onCurrentPageChange={handleOffsetChange}
          />
        )}
      </CntBox1>
      {deviceAddModalState && <DeviceAddModal />}
      {deleteModalState && <DeleteModal onConfirm={handleSelectDelete} />}
      {loadingState && <Loading />}
    </>
  );
};

export default Device;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const ReportBox = styled.div`
  margin-top: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  > h2 {
    font-size: 14px;
    color: #555;
  }
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 20px;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;
